import { Message } from "src/modules/communication-protobuf";
import avatarsSynchronizer from "src/modules/avatar/synchronizer";
import * as deviceSynchronizer from "src/modules/device/synchronizer";
import commandsSynchronizer from "src/modules/command/synchronizer";
import lullabiesSynchronizer from "src/modules/lullaby/synchronizer";
import {
  synchronizeCloudAccount,
  synchronizeCloudSettings
} from "src/modules/auth/duck/epics";
import * as cloud from "./ajax/cloud";
import { getPairedDevices } from "src/modules/device/duck/selectors/index";
import debug from "src/modules/debug-logger";
import SynchronizableItems from "./SynchronizableItemsEnum";
import environment from "src/utils/environment";

const log = window.require("electron-log");

export const ALL = "ALL";

export default function createCloudSynchronizer({ dispatch }) {
  let messenger = null;
  let syncRequestQueue = [];
  let synchronizing = false;

  async function synchronizeItem(item) {
    try {
      debug.synchronizer("About to sync", item);
      switch (item) {
        case SynchronizableItems.AVATARS:
          await dispatch(avatarsSynchronizer.synchronize());
          break;
        case SynchronizableItems.CLOUD_ACCOUNT:
          await dispatch(synchronizeCloudAccount());
          break;
        case SynchronizableItems.COMMANDS:
          if (environment.supportsCommands) {
            await dispatch(commandsSynchronizer.synchronize());
          }
          break;
        case SynchronizableItems.LULLABIES:
          if (environment.supportsLullabies) {
            await dispatch(lullabiesSynchronizer.synchronize());
          }
          break;
        case SynchronizableItems.CLOUD_SETTINGS:
          await dispatch(synchronizeCloudSettings());
          break;
        case SynchronizableItems.DEVICES:
          await dispatch(deviceSynchronizer.synchronize());
          break;
        default:
          break;
      }
      await cloud.confirmItemSync(item);
    } catch (err) {
      log.error(err);
    }
  }

  async function synchronize({ items, syncAll } = {}) {
    if (syncAll) {
      debug.synchronizer("syncAll items requested");

      items = Object.values(SynchronizableItems).reduce(
        function setAllItemsFalse(accu, itemName) {
          accu[itemName] = false;
          return accu;
        },
        {}
      );
    } else if (items == null) {
      items = await cloud.getSyncState();
    }

    const itemsToSync = Object.keys(items).filter(
      (itemName) => items[itemName] === false
    );

    debug.synchronizer("resolved items to sync", itemsToSync);

    if (synchronizing) {
      syncRequestQueue.push(items);
      return;
    } else {
      synchronizing = true;
    }

    await Promise.all(itemsToSync.map(synchronizeItem));

    synchronizing = false;

    if (syncRequestQueue[0]) {
      await synchronize({ items: syncRequestQueue.shift() });
    }
  }

  function handleCloudAccountSyncMessage({ item }, from) {
    debug.synchronizer("received CloudAccountSyncMessage for item", item);
    item === ALL
      ? synchronize({ syncAll: true })
      : synchronize({ items: { [item]: false } });
  }

  function announceItemChange(item) {
    return async (dispatch, getState) => {
      const state = getState();
      const familyDevices = Object.values(getPairedDevices(state)).filter(
        ({ status, group }) => group === "myDevices" && status !== "unavailable"
      );

      debug.synchronizer(
        `About to announceItemChange of ${item} to`,
        familyDevices
      );

      const message = Message.CloudAccountSyncMessage.create({ item });
      familyDevices.forEach(({ jid }) => {
        messenger.sendAsEnvelope({ to: jid, payload: { message } });
      });
    };
  }

  return {
    clearData: async () => {
      try {
        if (environment.supportsCommands) {
          await commandsSynchronizer.clearData();
        }
        if (environment.supportsLullabies) {
          await lullabiesSynchronizer.clearData();
        }
        await avatarsSynchronizer.clearData();
      } catch (error) {
        log.error(error);
      }
    },
    synchronize,
    synchronizeCloudAccount: () =>
      synchronize({ items: { [SynchronizableItems.CLOUD_ACCOUNT]: false } }),
    synchronizeLullabies: () =>
      synchronize({ items: { [SynchronizableItems.LULLABIES]: false } }),
    synchronizeDevices: () =>
      synchronize({ items: { [SynchronizableItems.DEVICES]: false } }),
    announceItemChange: (itemName) => dispatch(announceItemChange(itemName)),
    announceChange: () => dispatch(announceItemChange(ALL)),
    setMessenger(newMessenger) {
      messenger &&
        messenger.removeListener(
          Message.CloudAccountSyncMessage.name,
          handleCloudAccountSyncMessage
        );

      syncRequestQueue = [];

      messenger = newMessenger;
      messenger.on(
        Message.CloudAccountSyncMessage.name,
        handleCloudAccountSyncMessage
      );
    },
    SynchronizableItems
  };
}
